/**
 * useGlobalSearchShortcut — registers the ⌘K / Ctrl+K global shortcut that
 * opens the Command Palette (#419) from anywhere on a private screen.
 *
 * Mounted once by `PrivateLayout` (the single wrapper rendered by every
 * private route in `App.js`), so the shortcut works app-wide without every
 * page needing to know about it.
 *
 * @module hooks/useGlobalSearchShortcut
 */

import { useCallback, useEffect, useState } from 'react';

/**
 * @returns {{ isOpen: boolean, open: () => void, close: () => void }}
 */
export const useGlobalSearchShortcut = () => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isMod = event.metaKey || event.ctrlKey;
      if (isMod && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { isOpen, open, close };
};

export default useGlobalSearchShortcut;
